/**
 * Mundolibros Wishlist
 * Maneja la lista de deseos usando localStorage.
 * Los datos se guardan como array de IDs de producto.
 */

const WISHLIST_KEY = 'mundolibros_wishlist';

const Wishlist = {
  get() {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      // Compatibilidad con formato antiguo (array de strings)
      return raw.map((item) =>
        typeof item === 'string' ? { id: item, handle: null } : item
      );
    } catch {
      return [];
    }
  },

  save(list) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  },

  has(productId) {
    return this.get().some((item) => String(item.id) === String(productId));
  },

  add(productId, productHandle) {
    const list = this.get();
    const id = String(productId);
    if (!list.some((item) => String(item.id) === id)) {
      list.push({ id, handle: productHandle || null });
      this.save(list);
    }
    this._dispatch('wishlist:updated', { list });
  },

  remove(productId) {
    const id = String(productId);
    const list = this.get().filter((item) => String(item.id) !== id);
    this.save(list);
    this._dispatch('wishlist:updated', { list });
  },

  toggle(productId, productHandle) {
    this.has(productId) ? this.remove(productId) : this.add(productId, productHandle);
  },

  count() {
    return this.get().length;
  },

  _dispatch(event, detail) {
    document.dispatchEvent(new CustomEvent(event, { detail, bubbles: true }));
  },
};

/**
 * Web Component: <wishlist-button>
 * Uso: <wishlist-button data-product-id="123456789"></wishlist-button>
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle || null;
    this._render();
    this._syncState();

    this.querySelector('button').addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      Wishlist.toggle(this.productId, this.productHandle);
      this._syncState();
    });

    document.addEventListener('wishlist:updated', () => this._syncState());
  }

  _render() {
    this.innerHTML = `
      <button
        type="button"
        class="wishlist-btn"
        aria-label="Agregar a lista de deseos"
        title="Agregar a lista de deseos"
      >
        <svg class="wishlist-btn__icon wishlist-btn__icon--empty" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="20" height="20">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <svg class="wishlist-btn__icon wishlist-btn__icon--filled" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="20" height="20" style="display:none;">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
    `;
  }

  _syncState() {
    const active = Wishlist.has(this.productId);
    const btn = this.querySelector('button');
    const empty = this.querySelector('.wishlist-btn__icon--empty');
    const filled = this.querySelector('.wishlist-btn__icon--filled');

    if (!btn) return;

    btn.classList.toggle('wishlist-btn--active', active);
    btn.setAttribute(
      'aria-label',
      active ? 'Quitar de lista de deseos' : 'Agregar a lista de deseos'
    );
    btn.setAttribute(
      'title',
      active ? 'Quitar de lista de deseos' : 'Agregar a lista de deseos'
    );

    if (empty && filled) {
      empty.style.display = active ? 'none' : '';
      filled.style.display = active ? '' : 'none';
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Renderiza la grilla de productos en la página /pages/lista-de-deseos
 */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._render();
    document.addEventListener('wishlist:updated', () => this._render());
  }

  async _render() {
    const items = Wishlist.get();
    const container = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const counter = document.querySelector('[data-wishlist-count]');

    if (counter) counter.textContent = items.length;

    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    container.innerHTML = '<p style="opacity:.5;padding:2rem 0;">Cargando tu lista...</p>';

    try {
      const results = await Promise.all(
        items.map(({ id, handle }) => {
          if (handle) {
            return fetch(`/products/${handle}.json`)
              .then((r) => r.json())
              .then((d) => (d.product ? { ...d.product, _wishlistId: id } : null));
          }
          // Fallback para entradas antiguas sin handle
          return fetch(`/search/suggest.json?q=${encodeURIComponent(id)}&resources[type]=product&resources[limit]=1`)
            .then((r) => r.json())
            .then((d) => {
              const p = d.resources?.results?.products?.[0];
              return p ? { ...p, _wishlistId: id } : null;
            });
        })
      );

      const products = results.filter(Boolean);

      if (products.length === 0) {
        container.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }

      container.innerHTML = products
        .map(
          (p) => `
          <div class="wishlist-card">
            <div class="wishlist-card__image-wrapper">
              <a href="/products/${p.handle}">
                <img
                  src="${p.images?.[0]?.src || p.image?.src || ''}"
                  alt="${p.title}"
                  width="300"
                  height="375"
                  loading="lazy"
                  class="wishlist-card__image"
                >
              </a>
              <wishlist-button data-product-id="${p._wishlistId}" data-product-handle="${p.handle}" class="wishlist-card__btn"></wishlist-button>
            </div>
            <div class="wishlist-card__info">
              <p class="wishlist-card__vendor caption-with-letter-spacing">${p.vendor || ''}</p>
              <h3 class="wishlist-card__title h5">
                <a href="/products/${p.handle}">${p.title}</a>
              </h3>
              <p class="wishlist-card__price">${this._formatPrice(p.variants?.[0]?.price)}</p>
              <form method="post" action="/cart/add">
                <input type="hidden" name="id" value="${p.variants?.[0]?.id || ''}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="button button--primary button--full-width">
                  Agregar al carrito
                </button>
              </form>
            </div>
          </div>
        `
        )
        .join('');
    } catch {
      container.innerHTML =
        '<p style="opacity:.5;padding:2rem 0;">No se pudo cargar tu lista. Recargá la página.</p>';
    }
  }

  _formatPrice(cents) {
    if (!cents) return '';
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0,
    }).format(Math.round(cents));
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

// Actualizar contadores de wishlist en el header al cargar
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = Wishlist.count();
  });
});

window.Wishlist = Wishlist;
